"use client";

import * as React from "react";
import Image from "next/image";
import { Autoplay } from "swiper/modules";

import { extractStrapiMediaUrl } from "@/lib/strapi";
import { useSlidersStore } from "@/stores/slider";
import { AppSwiper, SwiperSlide } from "@/components/ui/swiper";
import type { Swiper as SwiperInstance } from "swiper/types";

type HeroMediaKind = "image" | "video";
type HeroMedia = {
  kind: HeroMediaKind;
  url: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function pickMimeFromStrapiMedia(value: unknown): string | null {
  if (!value) return null;
  if (!isRecord(value)) return null;

  const directMime = value["mime"];
  if (typeof directMime === "string" && directMime) return directMime;

  const attributes = value["attributes"];
  if (isRecord(attributes)) {
    const mime = attributes["mime"];
    if (typeof mime === "string" && mime) return mime;
  }

  const data = value["data"];
  if (data) return pickMimeFromStrapiMedia(data);

  return null;
}

function inferMediaKindFromUrl(url: string): HeroMediaKind {
  const normalized = url.split("?")[0]?.toLowerCase() ?? "";
  if (/\.(mp4|webm|ogg|mov|m4v)$/i.test(normalized)) return "video";
  return "image";
}

function extractStrapiMedia(value: unknown): HeroMedia | null {
  const url = extractStrapiMediaUrl(value);
  if (!url) return null;

  const mime = pickMimeFromStrapiMedia(value);
  const kind: HeroMediaKind =
    mime?.toLowerCase().startsWith("video/") ? "video" : mime?.toLowerCase().startsWith("image/") ? "image" : inferMediaKindFromUrl(url);

  return { kind, url };
}

function pickMediaFromCandidates(candidates: unknown[]): HeroMedia | null {
  for (const candidate of candidates) {
    const media = extractStrapiMedia(candidate);
    if (media?.kind === "video") return media;
  }

  for (const candidate of candidates) {
    const media = extractStrapiMedia(candidate);
    if (media) return media;
  }

  return null;
}

function pickHeroBackgroundMedia(attributes: Record<string, unknown>): { desktop: HeroMedia | null; mobile: HeroMedia | null } {
  const desktopCandidates = [
    attributes["background_video"],
    attributes["backgroundVideo"],
    attributes["video"],
    attributes["desktop_video"],
    attributes["desktopVideo"],
    attributes["background_image"],
    attributes["backgroundImage"],
    attributes["image"],
    attributes["desktop_image"],
    attributes["desktopImage"],
    attributes["media"],
  ];

  const mobileCandidates = [
    attributes["mobile_video"],
    attributes["mobileVideo"],
    attributes["mobile_image"],
    attributes["mobileImage"],
  ];

  const desktop = pickMediaFromCandidates(desktopCandidates);
  const mobile = pickMediaFromCandidates(mobileCandidates);

  return { desktop, mobile };
}

function isRemoteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

function normalizeAlt(attributes: Record<string, unknown>): string {
  const title = attributes["title"];
  if (typeof title === "string" && title.trim()) return title.trim();
  return "Hero banner";
}

function getYouTubeEmbedUrl(url: string): string | null {
  if (!url) return null;

  let videoId: string | null = null;

  // Regular expression to find a YouTube video ID
  const regex =
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/;
  const match = url.match(regex);

  if (match && match[1]) {
    videoId = match[1];
  }

  if (videoId) {
    const searchParams = new URLSearchParams();
    searchParams.set("autoplay", "1");
    searchParams.set("mute", "1");
    searchParams.set("loop", "1");
    searchParams.set("controls", "0");
    searchParams.set("showinfo", "0");
    searchParams.set("modestbranding", "1");
    searchParams.set("playsinline", "1");
    searchParams.set("disablekb", "1");
    searchParams.set("playlist", videoId); // Required for loop to work

    return `https://www.youtube.com/embed/${videoId}?${searchParams.toString()}`;
  }

  return null;
}

type HeroBannerItem = {
  key: string;
  desktopMedia: HeroMedia | null;
  mobileMedia: HeroMedia | null;
  alt: string;
  youtubeLink: string | null;
};

function pickHeroBannerItems(
  sliders: Array<{ attributes?: unknown }>,
): HeroBannerItem[] {
  return sliders
    .map((slider, index) => {
      const attributes = (slider.attributes ?? {}) as Record<string, unknown>;
      const { desktop, mobile } = pickHeroBackgroundMedia(attributes);
      const youtubeLink = attributes["youtube_link"];
      const embedUrl =
        typeof youtubeLink === "string" ? getYouTubeEmbedUrl(youtubeLink) : null;

      if (!desktop && !mobile && !embedUrl) return null;
      const id = (slider as { id?: unknown }).id;

      return {
        key:
          typeof id === "number" || typeof id === "string"
            ? String(id)
            : `${index}`,
        desktopMedia: desktop,
        mobileMedia: mobile,
        alt: normalizeAlt(attributes),
        youtubeLink: embedUrl,
      };
    })
    .filter((item): item is HeroBannerItem => item !== null);
}

export function HeroSection() {
  const { sliders, isLoading } = useSlidersStore();
  const [swiper, setSwiper] = React.useState<SwiperInstance | null>(null);

  const items = React.useMemo(() => pickHeroBannerItems(sliders), [sliders]);
  const isLoopEnabled = items.length > 1;

  React.useEffect(() => {
    if (!swiper || !isLoopEnabled) return;
    const activeItem = items[swiper.realIndex];
    const delay = activeItem?.youtubeLink ? 10000 : 4000;
    if (swiper.params.autoplay && typeof swiper.params.autoplay !== "boolean") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, react-hooks/immutability
      (swiper.params.autoplay as any).delay = delay;
    }
    swiper.autoplay.start();
  }, [swiper, items, isLoopEnabled]);
//   console.log(items)

  return (
    <section
      aria-label="Romano Intro"
      className="relative isolate w-full border-b border-primary/10 bg-primary/10"
    >
      {isLoading && items.length === 0 ? (
        <div className="absolute inset-0 animate-pulse">
          <div className="absolute inset-0 bg-muted/40" />
          <div className="absolute inset-x-0 top-1/2 mx-auto h-10 w-[min(560px,86vw)] -translate-y-1/2 rounded-2xl bg-muted" />
        </div>
      ) : null}
      {items.length ? (
        <AppSwiper
          onSwiper={setSwiper}
          onSlideChange={(s) => {
            if (!isLoopEnabled) return;
            const activeItem = items[s.realIndex];
            const delay = activeItem?.youtubeLink ? 10000 : 4000;
            if (s.params.autoplay && typeof s.params.autoplay !== "boolean") {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any, react-hooks/immutability
              (s.params.autoplay as any).delay = delay;
            }
            s.autoplay.start();
          }}
          className="w-full"
          swiperClassName="w-full px-0 sm:px-0 lg:px-0"
          showNavigation={false}
          slidesPerView={1}
          autoHeight
          loop={isLoopEnabled}
          speed={800}
          autoplay={
            isLoopEnabled
              ? {
                  delay: 4000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: false,
                }
              : false
          }
          modules={[Autoplay]}
        >
          {items.map((item, index) => (
            <SwiperSlide key={item.key} className="w-full">
              <div className="w-full">
                {item.youtubeLink ? (
                  <div className="relative w-full">
                    <iframe
                      src={item.youtubeLink}
                      className="h-auto w-full aspect-video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={item.alt}
                    ></iframe>
                    <div className="absolute inset-0" />
                  </div>
                ) : (
                  <>
                    {(() => {
                      const desktop = item.desktopMedia ?? item.mobileMedia;
                      if (!desktop) return null;
                      return (
                        <div className="hidden w-full md:block">
                          {desktop.kind === "video" ? (
                            <video
                              className="h-auto w-full"
                              autoPlay
                              muted
                              loop
                              playsInline
                              preload="auto"
                              aria-label={item.alt}
                            >
                              <source src={desktop.url} />
                            </video>
                          ) : (
                            <Image
                              src={desktop.url}
                              alt={item.alt}
                              width={1920}
                              height={1080}
                              className="h-auto w-full"
                              priority={index === 0}
                              sizes="100vw"
                              unoptimized={isRemoteUrl(desktop.url)}
                            />
                          )}
                        </div>
                      );
                    })()}

                    {(() => {
                      const mobile = item.mobileMedia ?? item.desktopMedia;
                      if (!mobile) return null;
                      return (
                        <div className="w-full md:hidden">
                          {mobile.kind === "video" ? (
                            <video
                              className="h-auto w-full"
                              autoPlay
                              muted
                              loop
                              playsInline
                              preload="auto"
                              aria-label={item.alt}
                            >
                              <source src={mobile.url} />
                            </video>
                          ) : (
                            <Image
                              src={mobile.url}
                              alt={item.alt}
                              width={1080}
                              height={1920}
                              className="h-auto w-full"
                              priority={index === 0}
                              sizes="100vw"
                              unoptimized={isRemoteUrl(mobile.url)}
                            />
                          )}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            </SwiperSlide>
          ))}
        </AppSwiper>
      ) : null}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(1200px_500px_at_50%_0%,rgba(255,255,255,0.55),transparent_60%),radial-gradient(900px_450px_at_50%_100%,rgba(0,0,0,0.18),transparent_55%)]" />
      <div className="pointer-events-none absolute inset-0 bg-linear-to-b from-white/10 via-transparent to-black/10" />

    </section>
  );
}
